import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminDataRoomListItem } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminDataRoomsQuery } from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatFullDate } from '@/utils/format'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/_protected/admin/datarooms/')({
  component: AdminDataRoomsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

const columns: TableColumn<AdminDataRoomListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (room) => (
      <CellLink to="/admin/datarooms/$id" params={{ id: room.id }} className="font-mono text-xs">
        {room.id.slice(0, 8)}
      </CellLink>
    ),
  },
  {
    header: 'Startup',
    render: (room) => <CellPrimary>{room.startupName}</CellPrimary>,
  },
  {
    header: 'Created By',
    render: (room) => <CellText>{room.createdByEmail ?? '—'}</CellText>,
  },
  {
    header: 'Token',
    width: '140px',
    render: (room) => <CellText className="font-mono text-xs">{room.token}</CellText>,
  },
  {
    header: 'Status',
    width: '100px',
    render: (room) => (
      <Badge variant={room.revokedAt ? 'destructive' : 'success'} size="sm">
        {room.revokedAt ? 'Revoked' : 'Active'}
      </Badge>
    ),
  },
  {
    header: 'Sessions',
    width: '80px',
    render: (room) => <span className="font-medium tabular-nums">{room.sessionsCount}</span>,
  },
  {
    header: 'Created',
    width: '130px',
    render: (room) => <CellText>{formatFullDate(room.createdAt)}</CellText>,
  },
]

function AdminDataRoomsPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const dataRoomsQuery = useAdminDataRoomsQuery({
    query: { page, limit: LIMIT, search: search || undefined },
  })

  const dataRooms = dataRoomsQuery.data?.data?.items ?? []
  const total = dataRoomsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <SearchInput
        placeholder="Search by startup name..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={dataRooms}
        isLoading={dataRoomsQuery.isLoading}
        emptyMessage="No data rooms found."
        keyExtractor={(r) => r.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
